import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TextButton from 'LuxKit/Button/TextButton'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import Group from 'components/utils/Group'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { OfferPageMultiRoomMode, OfferPageActions, OfferPageFilterType, OfferPageRoom, OfferPagePriceMode } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { hasIncludedGuestsExceeded } from 'lib/checkout/cartReservationUtils'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import moment from 'moment'
import { FLIGHTS_MAX_LEAD_TIME_DAYS } from 'constants/flight'
import React, { useCallback, useMemo } from 'react'
import { getCalendarsToBeFetchedByRoom, getIsLoadingByRoom, LoadingStatusByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import MobileMultipleRoomsDurationLabel from './MobileMultipleRoomsDurationLabel'
import { EmptyArray } from 'lib/array/arrayUtils'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { getPriceDisplayFunction } from 'lib/offer/offerPageUtil'
import MessageBanner from 'LuxKit/Banners/MessageBanner'

interface Props {
  offer: App.Offer
  checkIn?: string
  airports: Array<App.Airport>
  flightOrigin?: App.Airport
  isFlightsSelected?: boolean
  isFlightsEnabled: boolean
  isLoadingByRoom: LoadingStatusByRoom
  calendarsToBeFetchedByRoom: LoadingStatusByRoom
  room: App.RoomOccupants
  packageOption: App.OfferPackageOptionView
  ctaDirection?: 'horizontal' | 'vertical'
  children?: React.ReactNode
  mobileMultiRoomMode?: OfferPageMultiRoomMode
  rooms: Array<OfferPageRoom>
  priceMode: OfferPagePriceMode
  charterBundleRoute?: App.CharterBundleRoute
}

function MobileMultipleRoomsRoomOptionPriceDetails(props: Props) {
  const {
    offer,
    checkIn,
    airports,
    flightOrigin,
    isFlightsSelected,
    isLoadingByRoom,
    calendarsToBeFetchedByRoom,
    room,
    packageOption,
    ctaDirection = 'horizontal',
    children,
    mobileMultiRoomMode = 'individual',
    rooms,
    isFlightsEnabled,
    priceMode,
    charterBundleRoute,
  } = props

  const {
    package: pkg,
    hotelValue,
    hotelDiscountPercent,
    bundledDiscountPercent,
    hotelPrice,
    hotelMemberPrice,
    baseMemberPrice,
    propertyFees,
  } = packageOption

  const { totalFlightPrice, loading: charterFlightLoading } = useOfferPageFlightPrice()

  const missingCharterFlightPrice = !!charterBundleRoute && !totalFlightPrice
  const shouldShowCharterFlightErrorMessage = missingCharterFlightPrice && !charterFlightLoading

  let flightPrice = totalFlightPrice

  if (mobileMultiRoomMode === 'individual') {
    const roomPax = sumUpOccupancies([room])
    const totalPax = sumUpOccupancies(rooms)
    const flightPriceProportion = roomPax / totalPax

    flightPrice = totalFlightPrice * flightPriceProportion
  }

  const offerPageDispatch = useOfferPageDispatch()
  const hasFlights = !!flightPrice && !offer.flightCacheDisabled
  const showIncludedGuestsExceeded = useMemo(() => hasIncludedGuestsExceeded(room, pkg, offer) && !!pkg.roomPolicyDescription, [pkg, room, offer])
  const flightDestination = useMemo(() => airports?.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])

  const isFetching = isLoadingByRoom[room.id] || calendarsToBeFetchedByRoom[room.id]

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterType })
  }, [offerPageDispatch, pkg.roomType])

  const shouldShowDiscountPercent = hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const flightsBundledNoCache = !hasFlights && offer.bundledWithFlightsOnly
  const shouldShowBundledDiscountPercent = bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const highestDiscountPercent = hotelDiscountPercent > bundledDiscountPercent ? hotelDiscountPercent : bundledDiscountPercent

  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const departureTooFarInFuture = useMemo(() => !!checkInMoment && checkInMoment.diff(moment.utc(), 'days') > FLIGHTS_MAX_LEAD_TIME_DAYS, [checkInMoment])

  const totalHotelFlightsValue = hotelValue + flightPrice

  const totalPrice = hotelPrice + (propertyFees ?? 0)

  const totalMemberPrice = hotelMemberPrice ? hotelMemberPrice + (propertyFees ?? 0) : 0

  const totalFlightBundlePrice = hotelPrice + (propertyFees ?? 0) + flightPrice

  const totalMemberFlightBundlePrice = hotelMemberPrice ? hotelMemberPrice + (propertyFees ?? 0) + flightPrice : 0

  const showMemberPrice = baseMemberPrice > 0

  const shouldShowDiscount = (shouldShowDiscountPercent || shouldShowBundledDiscountPercent) && pkg.shouldDisplayValue && !offer.isDiscountPillHidden
  const shouldShowValue = hotelValue > (showMemberPrice ? hotelMemberPrice : hotelPrice) && pkg.shouldDisplayValue

  const onlyShowDiscount = shouldShowDiscount && ctaDirection === 'horizontal'
  const showValueAndDiscount = shouldShowValue && ctaDirection === 'vertical'

  const convertToDisplay = getPriceDisplayFunction(priceMode, mobileMultiRoomMode === 'individual' ? [room] : rooms)

  return (
    <Group
      direction={ctaDirection}
      horizontalAlign={ctaDirection === 'horizontal' ? 'space-between' : 'start'}
      verticalAlign={ctaDirection === 'horizontal' ? 'end' : 'start'}
      gap={ctaDirection === 'horizontal' ? 20 : 12}
    >
      <Group direction="vertical" gap={12}>
        <LoadingIndicator visible={isFetching} floating opaque />
        {!offer.bundledWithFlightsOnly && !isFlightsSelected && (
          <Group direction="vertical" gap={8}>
            {onlyShowDiscount && <PriceRowValueDiscount
              data-testid="value-box"
              discountPercentage={highestDiscountPercent}
            />}
            <div data-testid="pricing">
              <PriceRowPriceCaption><MobileMultipleRoomsDurationLabel shouldDisplayRoom={mobileMultiRoomMode === 'same-room'} /></PriceRowPriceCaption>
              <LuxPlusPriceStack
                size="S"
                price={convertToDisplay(totalPrice)}
                memberPrice={convertToDisplay(totalMemberPrice)}
                saleUnit={priceMode === 'perPerson' ? 'person' : 'total'}
                offerConfig={offer.luxPlus}
              />
              {showValueAndDiscount && <PriceRowValueDiscountWithCaption
                data-testid="value-box"
                size="M"
                originalValue={convertToDisplay(hotelValue)}
                discountPercentage={shouldShowDiscount ? highestDiscountPercent : undefined}
              />}
              {showIncludedGuestsExceeded && (
                <AdditionalGuestsPopup
                  complex={false}
                  description="+ Extra guest surcharge"
                  modalContent={pkg.roomPolicyDescription}
                />
              )}
            </div>
          </Group>
        )}
        {isFlightsEnabled && isFlightsSelected && (
          <Group direction="vertical" gap={12}>
            {(() => {
              if (hasFlights) {
                return <Group direction="vertical" gap={8}>
                  {onlyShowDiscount && <PriceRowValueDiscount
                    data-testid="value-box"
                    discountPercentage={highestDiscountPercent}
                  />}
                  <div data-testid="pricing">
                    <PriceRowPriceCaption><MobileMultipleRoomsDurationLabel shouldDisplayRoom={mobileMultiRoomMode === 'same-room'} /></PriceRowPriceCaption>
                    <LuxPlusPriceStack
                      size="S"
                      price={convertToDisplay(totalFlightBundlePrice)}
                      memberPrice={convertToDisplay(totalMemberFlightBundlePrice)}
                      saleUnit={priceMode === 'perPerson' ? 'person' : 'total'}
                      offerConfig={offer.luxPlus}
                    />
                    {showValueAndDiscount && <PriceRowValueDiscountWithCaption
                      data-testid="value-box"
                      size="M"
                      originalValue={convertToDisplay(totalHotelFlightsValue)}
                      discountPercentage={shouldShowDiscount ? highestDiscountPercent : undefined}
                    />}
                    {showIncludedGuestsExceeded && (
                      <AdditionalGuestsPopup
                        complex={false}
                        description="+ Extra guest surcharge"
                        modalContent={pkg.roomPolicyDescription}
                      />
                    )}
                  </div>
                </Group>
              }
              else if (shouldShowCharterFlightErrorMessage) {
                return <MessageBanner kind="critical">
                  Failed to find charter flight data
                </MessageBanner>
              }
              else if (flightOrigin?.code === flightDestination?.code) {
                return <Group direction="vertical" gap={8}>
                  <PriceRowCaption>
                    No flights available from {flightOrigin?.name}. Please select another departing airport.
                  </PriceRowCaption>
                  <TextButton
                    kind="tertiary"
                    data-filter="airport"
                    onClick={onToggleFilter}
                  >
                    Change airport
                  </TextButton>
                </Group>
              }
              else if (departureTooFarInFuture) {
                return <PriceRowCaption>
                  Flights can only be booked up to 11 months in advance.
                </PriceRowCaption>
              }
              else if (!flightsBundledNoCache) {
                return <Group direction="vertical" gap={8}>
                  {onlyShowDiscount && <PriceRowValueDiscount
                    data-testid="value-box"
                    discountPercentage={highestDiscountPercent}
                  />}
                  <div>
                    <PriceRowPriceCaption><MobileMultipleRoomsDurationLabel shouldDisplayRoom={mobileMultiRoomMode === 'same-room'} /></PriceRowPriceCaption>
                    <LuxPlusPriceStack
                      size="S"
                      price={convertToDisplay(totalFlightBundlePrice)}
                      saleUnit={priceMode === 'perPerson' ? 'person*' : 'total*'}
                      memberPrice={convertToDisplay(totalMemberFlightBundlePrice)}
                      offerConfig={offer.luxPlus}
                    />
                    {showValueAndDiscount && <PriceRowValueDiscountWithCaption
                      data-testid="value-box"
                      size="M"
                      originalValue={convertToDisplay(totalHotelFlightsValue)}
                      discountPercentage={shouldShowDiscount ? highestDiscountPercent : undefined}
                    />}
                    <PriceRowTaxesAndFees />
                  </div>
                </Group>
              }
            })()}
            <AtolProtectedTooltip />
          </Group>
        )}
      </Group>

      {!!children && children}
    </Group>
  )
}

type MappedProps = Pick<Props,
  'offer'
  | 'checkIn'
  | 'airports'
  | 'flightOrigin'
  | 'isFlightsSelected'
  | 'isFlightsEnabled'
  | 'isLoadingByRoom'
  | 'calendarsToBeFetchedByRoom'
  | 'mobileMultiRoomMode'
  | 'rooms'
  | 'priceMode'
  | 'charterBundleRoute'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  offer: state.offer!,
  checkIn: state.checkInDate,
  airports: appState.geo.airports,
  flightOrigin: state.airport,
  isFlightsEnabled: !!state.offer?.offerFlightsEnabled,
  isFlightsSelected: state.flights,
  isLoadingByRoom: getIsLoadingByRoom(state, appState),
  calendarsToBeFetchedByRoom: getCalendarsToBeFetchedByRoom(state, appState),
  mobileMultiRoomMode: state.multiRoomMode,
  rooms: state.rooms ?? EmptyArray,
  priceMode: state.priceMode,
  charterBundleRoute: state.charterBundleRoute,
}), MobileMultipleRoomsRoomOptionPriceDetails)
